import {
  ChatMessage as ChatMessageType,
  FeedbackType,
} from 'interfaces/chat.interface'
import MessageFeedback from 'components/message-feedback/message-feedback.component'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

type ChatMessageProps = {
  message: ChatMessageType
  showProvider?: boolean
  showContext?: boolean
  onFeedbackChange?: (
    messageId: string,
    feedbackType: FeedbackType,
    reason: string | null
  ) => Promise<void>
}

const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  showProvider = false,
  showContext = false,
  onFeedbackChange,
}) => {
  const isFollowUp = message.message_type === 'follow_up'

  return (
    <div className="w-full flex justify-start mb-4 animate-fade-in">
      <div
        className={`transition-all duration-300 ease-in-out ${
          isFollowUp
            ? 'bg-background border-l-4 border-action p-4'
            : message.isWelcomeMessage
            ? 'pt-4 pr-4 pl-4 pb-1'
            : 'p-4'
        }`}
      >
        <div className="text-text-heading mb-2 text-base font-normal leading-6 markdown-content">
          <ReactMarkdown 
            remarkPlugins={[remarkGfm]}
            components={{
              // Customize how bold text is rendered - inline bold for text within sentences
              strong: ({children}) => (
                <strong className="font-bold text-text-heading">{children}</strong>
              ),
              // Ensure proper paragraph spacing and preserve line breaks
              p: ({children}) => (
                <p className="mb-3 last:mb-0 whitespace-pre-wrap leading-relaxed">{children}</p>
              ),
              // Handle line breaks properly
              br: () => <br />,
              // Style lists appropriately
              ul: ({children}) => (
                <ul className="ml-4 mb-3 space-y-1">{children}</ul>
              ),
              ol: ({children}) => (
                <ol className="ml-4 mb-3 space-y-1 list-decimal">{children}</ol>
              ),
              li: ({children}) => (
                <li className="text-text-heading">{children}</li>
              ),
              // Ensure other elements don't interfere with spacing
              h1: ({children}) => <h1 className="font-bold text-lg mb-2">{children}</h1>,
              h2: ({children}) => <h2 className="font-bold text-base mb-2">{children}</h2>,
              h3: ({children}) => <h3 className="font-bold text-sm mb-1">{children}</h3>,
            }}
          >
            {message.content}
          </ReactMarkdown>
        </div>

        {/* Bottom section with timestamp/badges and feedback */}
        <div className="flex items-center justify-between mt-2">
          {/* Left side: Feedback (only for assistant messages with ID, excluding welcome messages) */}
          <div className="flex items-center w-full">
            {message.role === 'assistant' &&
              message.id &&
              onFeedbackChange &&
              !message.isWelcomeMessage && (
                <MessageFeedback
                  messageId={message.id.toString()}
                  currentFeedback={message.feedback || null}
                  onFeedbackChange={onFeedbackChange}
                  className="mr-2"
                />
              )}
          </div>

          {/* Right side: Timestamp and badges */}
          {(showProvider || showContext) && (
            <div className="flex flex-wrap justify-end">
              {isFollowUp && (
                <span className="text-xs font-medium bg-action text-white px-2 py-1 rounded-full">
                  Follow-up
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ChatMessage
